import * as XLSX from 'xlsx';

/**
 * Export data to Excel file
 * @param {Array} data - Array of objects to export
 * @param {Array} columns - Column configuration with { title, dataIndex, render? }
 * @param {string} filename - Name of the file (without extension)
 * @param {string} sheetName - Name of the sheet (default: 'Data')
 */
export const exportToExcel = (data, columns, filename, sheetName = 'Data') => {
  // Transform data using column configuration
  const exportData = data.map(row => {
    const exportRow = {};
    columns.forEach(col => {
      if (col.dataIndex) {
        let value = row[col.dataIndex];

        // Handle nested properties (e.g., 'category.name')
        if (col.dataIndex.includes('.')) {
          const keys = col.dataIndex.split('.');
          value = keys.reduce((obj, key) => obj?.[key], row);
        }

        // Use custom export formatter if provided
        if (col.exportRender) {
          value = col.exportRender(value, row);
        } else if (col.render && !col.skipRenderForExport) {
          // Try to extract text value from render function result
          const rendered = col.render(value, row);
          if (typeof rendered === 'string' || typeof rendered === 'number') {
            value = rendered;
          }
        }

        // Convert undefined/null to empty string
        if (value === undefined || value === null) {
          value = '';
        }

        exportRow[col.title] = value;
      }
    });
    return exportRow;
  });

  // Create workbook and worksheet
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.json_to_sheet(exportData);

  // Auto-size columns
  const colWidths = columns.map(col => ({
    wch: Math.max(
      col.title.length,
      ...exportData.map(row => String(row[col.title] || '').length)
    ) + 2
  }));
  ws['!cols'] = colWidths;

  // Add worksheet to workbook
  XLSX.utils.book_append_sheet(wb, ws, sheetName);

  // Generate filename with timestamp
  const timestamp = new Date().toISOString().slice(0, 10);
  const fullFilename = `${filename}_${timestamp}.xlsx`;

  // Save file
  XLSX.writeFile(wb, fullFilename);
};

/**
 * Export multiple sheets to single Excel file
 * @param {Array} sheets - Array of { data, columns, sheetName }
 * @param {string} filename - Name of the file (without extension)
 */
export const exportMultipleSheets = (sheets, filename) => {
  const wb = XLSX.utils.book_new();

  sheets.forEach(({ data, columns, sheetName }) => {
    const exportData = data.map(row => {
      const exportRow = {};
      columns.forEach(col => {
        if (col.dataIndex) {
          let value = row[col.dataIndex];

          if (col.exportRender) {
            value = col.exportRender(value, row);
          }

          if (value === undefined || value === null) {
            value = '';
          }

          exportRow[col.title] = value;
        }
      });
      return exportRow;
    });

    const ws = XLSX.utils.json_to_sheet(exportData);

    // Auto-size columns
    const colWidths = columns.map(col => ({
      wch: Math.max(
        col.title.length,
        ...exportData.map(row => String(row[col.title] || '').length)
      ) + 2
    }));
    ws['!cols'] = colWidths;

    XLSX.utils.book_append_sheet(wb, ws, sheetName);
  });

  const timestamp = new Date().toISOString().slice(0, 10);
  const fullFilename = `${filename}_${timestamp}.xlsx`;
  XLSX.writeFile(wb, fullFilename);
};

export default exportToExcel;
